import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  SmsConnectionCheckFieldsFragment,
  SmsProviderStateDocument,
  SmsProviderStateQuery,
  SmsProviderStateQueryVariables,
  SmsProviderStatusFieldsFragment,
  SmsSettingsFieldsFragment,
  SmsSettingsViewDocument,
  SmsSettingsViewQuery,
  useDispatchSmsTestMutation,
  useProbeSmsConnectionMutation,
  useSmsProviderStateQuery,
  useSmsSettingsViewQuery,
  useStoreSmsSettingsMutation,
  useStoreSmsTextsMutation,
} from 'core/graphql';

import { ISmsError, smsCrashErrors, toSmsErrors } from './smsFeedback';
import { ISmsSettingsInput } from './smsModel';
import { useRereadSmsSettings } from './useLinkAddresses';
import { useSmsPermissions } from './useSmsPermissions';

/**
 * ТЗ 0081 — настройки канала SMS, состояние SMSAPI (баланс, имя
 * отправителя), проверка подключения без отправки и настоящая тестовая SMS.
 *
 * У `SmsSettingsType` и `SmsProviderStatusType` нет `id`: ответы мутаций
 * пишутся в корневые поля руками, иначе экран показывал бы старое до
 * следующего запроса.
 */

export type ISmsSettings = SmsSettingsFieldsFragment;
export type ISmsProviderStatus = SmsProviderStatusFieldsFragment;
export type ISmsConnectionCheck = SmsConnectionCheckFieldsFragment;

/** Переменные базового запроса состояния — в них же пишется «Обновить». */
const PROVIDER_VARIABLES: SmsProviderStateQueryVariables = { refresh: false };

export interface ISmsCheckResult {
  ok: boolean;
  errors: ISmsError[];
  result: ISmsConnectionCheck | null;
}

export const useSmsSettings = () => {
  const { canUse, isAdmin } = useSmsPermissions();
  const client = useApolloClient();
  const settingsQuery = useSmsSettingsViewQuery({
    skip: !canUse,
    fetchPolicy: 'cache-and-network',
  });
  const providerQuery = useSmsProviderStateQuery({
    variables: PROVIDER_VARIABLES,
    skip: !canUse,
    fetchPolicy: 'cache-and-network',
  });
  const [saveMut, { loading: saving }] = useStoreSmsSettingsMutation();
  const [saveTextsMut, { loading: savingTexts }] = useStoreSmsTextsMutation();
  const [probeMut, { loading: checking }] = useProbeSmsConnectionMutation();
  const [testMut, { loading: testing }] = useDispatchSmsTestMutation();
  const [refreshing, setRefreshing] = useState(false);
  const reread = useRereadSmsSettings();

  const writeStatus = useCallback(
    (status: ISmsProviderStatus | null | undefined) => {
      if (!status) return;
      client.writeQuery<SmsProviderStateQuery, SmsProviderStateQueryVariables>({
        query: SmsProviderStateDocument,
        variables: PROVIDER_VARIABLES,
        data: { smsProviderStatus: status },
      });
    },
    [client],
  );

  const save = useCallback(
    async (
      input: ISmsSettingsInput,
    ): Promise<{ ok: boolean; errors: ISmsError[] }> => {
      try {
        const { data } = await saveMut({
          variables: { input },
          update: (cache, { data: answer }) => {
            const next = answer?.saveSmsSettings?.smsSettings;
            if (!next) return;
            cache.writeQuery<SmsSettingsViewQuery>({
              query: SmsSettingsViewDocument,
              data: { smsSettings: next },
            });
          },
        });
        const payload = data?.saveSmsSettings;
        const errors = toSmsErrors(payload?.customErrors);
        const ok = Boolean(payload?.success) && !errors.length;
        if (ok) reread();
        return { ok, errors };
      } catch (caught) {
        return { ok: false, errors: smsCrashErrors(caught) };
      }
    },
    [reread, saveMut],
  );

  /**
   * ТЗ 0084: хвост отписки рассылки по группе — Admin и продавцы
   * (`canEditTexts`). Пустая строка — вернуть текст по умолчанию. В хвосте
   * обязательна `{link}/stop` (иначе `STOP_LINK_REQUIRED`).
   */
  const saveTexts = useCallback(
    async (
      optOutSuffix: string,
    ): Promise<{ ok: boolean; errors: ISmsError[] }> => {
      try {
        const { data } = await saveTextsMut({
          variables: { optOutSuffix },
          update: (cache, { data: answer }) => {
            const next = answer?.saveSmsTexts?.smsSettings;
            if (!next) return;
            cache.writeQuery<SmsSettingsViewQuery>({
              query: SmsSettingsViewDocument,
              data: { smsSettings: next },
            });
          },
        });
        const payload = data?.saveSmsTexts;
        const errors = toSmsErrors(payload?.customErrors);
        const ok = Boolean(payload?.success) && !errors.length;
        if (ok) reread();
        return { ok, errors };
      } catch (caught) {
        return { ok: false, errors: smsCrashErrors(caught) };
      }
    },
    [reread, saveTextsMut],
  );

  /** «Обновить»: спросить SMSAPI сейчас (сервер пускает только Admin). */
  const refreshProvider = useCallback(async (): Promise<ISmsError[]> => {
    setRefreshing(true);
    try {
      const { data } = await client.query<
        SmsProviderStateQuery,
        SmsProviderStateQueryVariables
      >({
        query: SmsProviderStateDocument,
        variables: { refresh: true },
        // `no-cache`: ответ ложится в запись «без обновления» руками ниже,
        // а своей записи `refresh: true` в кэше не заводит.
        fetchPolicy: 'no-cache',
      });
      writeStatus(data?.smsProviderStatus);
      return [];
    } catch (caught) {
      return smsCrashErrors(caught);
    } finally {
      setRefreshing(false);
    }
  }, [client, writeStatus]);

  const checkConnection = useCallback(
    async (to: string): Promise<ISmsCheckResult> => {
      try {
        const { data } = await probeMut({ variables: { to: to.trim() } });
        const payload = data?.checkSmsConnection;
        writeStatus(payload?.status);
        const errors = toSmsErrors(payload?.customErrors);
        return {
          ok: Boolean(payload?.success) && Boolean(payload?.result?.ok),
          errors,
          result: payload?.result ?? null,
        };
      } catch (caught) {
        return { ok: false, errors: smsCrashErrors(caught), result: null };
      }
    },
    [probeMut, writeStatus],
  );

  const sendTest = useCallback(
    async (to: string, text: string): Promise<ISmsCheckResult> => {
      try {
        const { data } = await testMut({
          variables: { to: to.trim(), text: text.trim() || null },
        });
        const payload = data?.sendSmsTest;
        const errors = toSmsErrors(payload?.customErrors);
        return {
          ok: Boolean(payload?.success) && Boolean(payload?.result?.ok),
          errors,
          result: payload?.result ?? null,
        };
      } catch (caught) {
        return { ok: false, errors: smsCrashErrors(caught), result: null };
      }
    },
    [testMut],
  );

  const { refetch } = settingsQuery;
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  return {
    canUse,
    isAdmin,
    settings: settingsQuery.data?.smsSettings ?? null,
    loading: settingsQuery.loading && !settingsQuery.data,
    error: settingsQuery.error,
    reload,
    provider: providerQuery.data?.smsProviderStatus ?? null,
    providerLoading: providerQuery.loading && !providerQuery.data,
    save,
    saveTexts,
    refreshProvider,
    checkConnection,
    sendTest,
    busy: { saving, savingTexts, checking, testing, refreshing },
  };
};
